import React, { useState } from 'react'
import { useAuth } from '../context/AuthContext'
import styles from './RegisterPage.module.css'

export function RegisterPage() {
  const { isAuthenticated, requestOtp, verifyOtp } = useAuth()

  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [mobile, setMobile] = useState('')
  const [otp, setOtp] = useState('')
  const [step, setStep] = useState<'enter' | 'verify'>('enter')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  if (isAuthenticated) return null

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setInfo(null)
    setLoading(true)

    try {
      const response = await requestOtp({ email, name, mobile })
      if (response?.otp) {
        setInfo(`Temporary verification code: ${response.otp}`)
      } else {
        setInfo('Check your email for a one-time code.')
      }
      setStep('verify')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Request failed')
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      await verifyOtp({ email, otp })
      // on success AuthContext will store token and redirect
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Verification failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <span className={styles.logo} aria-hidden>
            📊
          </span>
          <h1 className={styles.title}>Join Facebook CampaignOS</h1>
          <p className={styles.subtitle}>Enter your company email to get a sign-in link</p>
        </div>

        <form className={styles.form} onSubmit={step === 'enter' ? handleRequest : handleVerify}>
          {step === 'enter' && (
            <>
              <div className={styles.field}>
                <label htmlFor="name" className={styles.label}>
                  Full name
                </label>
                <input
                  id="name"
                  type="text"
                  className={styles.input}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  required
                  disabled={loading}
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="email" className={styles.label}>
                  Work email
                </label>
                <input
                  id="email"
                  type="email"
                  className={styles.input}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@company.com"
                  required
                  disabled={loading}
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="mobile" className={styles.label}>
                  Phone number
                </label>
                <input
                  id="mobile"
                  type="tel"
                  className={styles.input}
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  required
                  disabled={loading}
                />
              </div>
            </>
          )}

          {step === 'verify' && (
            <div className={styles.field}>
              <label htmlFor="otp" className={styles.label}>
                Verification code
              </label>
              <input
                id="otp"
                type="text"
                className={styles.input}
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="123456"
                required
                disabled={loading}
                autoComplete="off"
              />
            </div>
          )}

          {error && (
            <div className={styles.error} role="alert">{error}</div>
          )}

          {info && (
            <div className={styles.help}>{info}</div>
          )}

          <button
            type="submit"
            className={styles.submitBtn}
            disabled={loading || (step === 'enter' && (!email || !name || !mobile)) || (step === 'verify' && !otp)}
          >
            {loading ? 'Working...' : step === 'enter' ? 'Send code' : 'Verify and continue'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default RegisterPage
